export type DisplayDateFormatId = 'ISO_DATE' | 'DAY_MON_YEAR' | 'MON_DAY_YEAR';
export type DisplayTimeFormatId = 'TIME_24' | 'TIME_12';
export type DisplayDateTimeFormatId =
  | 'ISO_DATETIME'
  | 'ISO_DATETIME_TIME_12'
  | 'DAY_MON_YEAR_TIME_24'
  | 'DAY_MON_YEAR_TIME_12';

export const DEFAULT_DISPLAY_DATE_FORMAT: DisplayDateFormatId = 'ISO_DATE';
export const DEFAULT_DISPLAY_TIME_FORMAT: DisplayTimeFormatId = 'TIME_24';
export const DEFAULT_DISPLAY_DATETIME_FORMAT: DisplayDateTimeFormatId = 'ISO_DATETIME';

export interface DisplayFormatOption<T extends string = string> {
  value: T;
  label: string;
  example: string;
}

export const DISPLAY_DATE_FORMAT_OPTIONS: DisplayFormatOption<DisplayDateFormatId>[] = [
  { value: 'ISO_DATE', label: 'ISO date', example: '2026-06-17' },
  { value: 'DAY_MON_YEAR', label: 'Day month year', example: '05 Jun 2026' },
  { value: 'MON_DAY_YEAR', label: 'Month day year', example: 'Jun 05, 2026' },
];

export const DISPLAY_TIME_FORMAT_OPTIONS: DisplayFormatOption<DisplayTimeFormatId>[] = [
  { value: 'TIME_24', label: '24-hour', example: '17:00:00' },
  { value: 'TIME_12', label: '12-hour', example: '5:00 PM' },
];

export const DISPLAY_DATETIME_FORMAT_OPTIONS: DisplayFormatOption<DisplayDateTimeFormatId>[] = [
  { value: 'ISO_DATETIME', label: 'ISO date-time (24-hour)', example: '2026-06-17 17:00:00' },
  { value: 'ISO_DATETIME_TIME_12', label: 'ISO date-time (12-hour)', example: '2026-06-17 5:00 PM' },
  { value: 'DAY_MON_YEAR_TIME_24', label: 'Day month year, 24-hour', example: '05 Jun 2026, 17:00:00' },
  { value: 'DAY_MON_YEAR_TIME_12', label: 'Day month year, 12-hour', example: '05 Jun 2026, 5:00 PM' },
];

/** Dropdown label for date / date-time presets — show the example pattern only. */
export function displayFormatExampleLabel(option: DisplayFormatOption): string {
  return option.example;
}

export function normalizeDisplayDateFormat(value?: string | null): DisplayDateFormatId {
  return normalizePreset(
    value ?? undefined,
    DISPLAY_DATE_FORMAT_OPTIONS.map((o) => o.value),
    DEFAULT_DISPLAY_DATE_FORMAT,
  );
}

export function normalizeDisplayTimeFormat(value?: string | null): DisplayTimeFormatId {
  return normalizePreset(
    value ?? undefined,
    DISPLAY_TIME_FORMAT_OPTIONS.map((o) => o.value),
    DEFAULT_DISPLAY_TIME_FORMAT,
  );
}

export function normalizeDisplayDateTimeFormat(value?: string | null): DisplayDateTimeFormatId {
  return normalizePreset(
    value ?? undefined,
    DISPLAY_DATETIME_FORMAT_OPTIONS.map((o) => o.value),
    DEFAULT_DISPLAY_DATETIME_FORMAT,
  );
}

function normalizePreset<T extends string>(value: string | undefined, allowed: readonly T[], fallback: T): T {
  const upper = (value ?? '').trim().toUpperCase();
  return allowed.includes(upper as T) ? (upper as T) : fallback;
}
